<template>
  <div class="flex flex-col">
    <div class="flex flex-col gap-6 lg:gap-8">
      <SelfFeedbackTandemItem
        v-if="selfFeedbackTandem"
        :number-of-feedbacks="localSelfFeedbackCount"
        :state="selfFeedbackTandem.state"
        :total-number-of-questions="numberOfQuestions"
        :survey-id="surveyId"
      />
      <div
        v-for="group in perspectiveGroups"
        :key="group.perspective.perspectiveKey"
        class="flex flex-col"
      >
        <div class="text-on-surface-variant mb-1 flex items-center">
          <BaseIcon class="mr-2">{{ group.perspective.icon }}</BaseIcon>
          <h3 class="text-title-medium">{{ group.perspective.label }}</h3>
        </div>
        <div class="flex flex-col gap-4">
          <ul v-if="group.personalTandems.length > 0" class="flex flex-col gap-0.5">
            <li
              v-for="tandem in group.personalTandems"
              :key="tandem.id"
              class="bg-surface-container rounded-medium first:rounded-t-large last:rounded-b-large overflow-hidden"
            >
              <SurveyTandemItem
                :member="tandem.sender!"
                :number-of-feedbacks="tandem.numberOfFeedbacks"
                :total-number-of-questions="numberOfQuestions"
                :survey-id="surveyId"
                :state="tandem.state"
                :rejection-reason="tandem.rejectionReason"
              />
            </li>
          </ul>
          <ul v-if="group.surveyLinks.length > 0" class="flex flex-col gap-0.5">
            <li
              v-for="link in group.surveyLinks"
              :key="link.access.token"
              class="bg-surface-container rounded-medium first:rounded-t-large last:rounded-b-large overflow-hidden"
            >
              <ExternalFeedbackItem
                :number-of-feedbacks="link.numberOfFeedbacks"
                :url="link.url"
                :title="$t('survey.surveyLinks.title')"
                :is-personal="link.isPersonal"
                :subtitle="link.memberName"
                @open-q-r-code-dialog="() => emits('openQRCodeDialog', link.url)"
              />
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import type { SurveyTandem } from '../models/survey'
  import type { PerspectiveGroup } from '../composables/useSurvey'
  import ExternalFeedbackItem from './ExternalFeedbackItem.vue'
  import SelfFeedbackTandemItem from './SelfFeedbackTandemItem.vue'
  import SurveyTandemItem from './SurveyTandemItem.vue'
  import { computed } from 'vue'
  import { useLocalFeedbacks } from '@/core/composables/useLocalFeedbacks.ts'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'

  interface Props {
    surveyId: string
    numberOfQuestions: number
    perspectiveGroups: ReadonlyArray<PerspectiveGroup>
    selfFeedbackTandem: SurveyTandem | undefined
  }

  const props = defineProps<Props>()

  const emits = defineEmits<{
    (e: 'openQRCodeDialog', url: string): void
  }>()

  const localSelfFeedbackCount = computed(() => {
    if (!props.selfFeedbackTandem) {
      return 0
    }
    const { feedbacksBySurveyIdAndReceiverId } = useLocalFeedbacks()
    return feedbacksBySurveyIdAndReceiverId(
      props.surveyId,
      props.selfFeedbackTandem.receiver.id ?? '',
    ).value.length
  })
</script>
